// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// Enums
import { MatrixQuestionDataType } from 'engagementReport/enums/propTypes';

// Utilities
import { colors, rtlLanguage, truncateString } from 'common/utils/helpers';

// Hooks
import useMatrixBarChartData from 'engagementReport/hooks/useMatrixBarChartData';

// Components
import MatrixTooltip from 'engagementReport/components/matrix/MatrixTooltip';
import YAxisAutoScaleLabel from 'engagementReport/components/base/YAxisAutoScaleLabel';

const MatrixHorizontalBarChart = ({ data, width }) => {
  const chartData = useMatrixBarChartData(data);

  if (!width) return null;

  /* eslint-disable react/prop-types */
  return (
    <>
      <ResponsiveContainer width="100%" aspect={16 / 9}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, bottom: 20 }} layout="vertical">
          <CartesianGrid horizontal={false} stroke="#e8e8e8" strokeWidth={1} />
          <XAxis
            type="number"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickLine={false}
            unit="%"
            interval={0}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tickFormatter={(label) => truncateString(label, 30)}
            tickLine={false}
            stroke="#636363"
            strokeWidth={1}
            interval={0}
            width={width * 0.3}
            fontSize={14}
            orientation={rtlLanguage() ? 'right' : 'left'}
            tick={<YAxisAutoScaleLabel parentWidth={width * 0.3} truncate={20} />}
          />
          <Tooltip
            content={<MatrixTooltip weights={data.weights} />}
            cursor={{ fill: 'rgba(100, 100, 100, 0.05)' }}
            isAnimationActive={false}
          />
          {data.weights.map(({ id, label }, index) => (
            <Bar
              key={id}
              weightId={id}
              name={label}
              dataKey={`values.${id}.percentage`}
              barSize={20}
              fill={colors[index % colors.length]}
              unit="%"
              stackId="meow"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <Legend
        layout="vertical"
        width="100%"
        wrapperStyle={{ marginTop: '20px', position: 'static' }}
        payload={data.weights.map(({ id, label }, index) => ({
          id,
          type: 'square',
          value: label,
          color: colors[index % colors.length],
        }))}
      />
    </>
  );
  /* eslint-enable react/prop-types */
};

Legend.propTypes = {
  width: PropTypes.string.isRequired,
};

MatrixHorizontalBarChart.propTypes = {
  data: MatrixQuestionDataType.isRequired,
  width: PropTypes.number,
};

MatrixHorizontalBarChart.defaultProps = {
  width: null,
};

export default MatrixHorizontalBarChart;
